import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useCallback } from "react";

import axios, { type AxiosResponse } from "axios";

import type { FacilityWithCareTeamsProps } from "../Components/CareTeams/types";
import type { ProviderDirectoryFacilityCareTeamList200Response } from "~/src/types";

export const DEFAULT_SEARCH_PARAMS =
  "care_team.care_team_type=Primary%20Care&care_team.care_team_type=Pediatric";

export interface FacilityCareTeamData {
  results: FacilityWithCareTeamsProps[];
  loading: boolean;
  error: string | null;
}

interface FacilityCareTeamContextType {
  facilityCareTeamData: FacilityCareTeamData;
  fetchFacilityCareTeams: (searchParams: string) => void;
  setFacilityCareTeams: (data: Partial<FacilityCareTeamData>) => void;
  facilityCareTeamResults: FacilityWithCareTeamsProps[];
  setFacilityCareTeamResults: (results: FacilityWithCareTeamsProps[]) => void;
}

const FacilityCareTeamContext = createContext<
  FacilityCareTeamContextType | undefined
>(undefined);

export const FacilityCareTeamProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [facilityCareTeamData, setFacilityCareTeamData] =
    useState<FacilityCareTeamData>({
      results: [],
      loading: true,
      error: null,
    });

  const fetchFacilityCareTeams = useCallback((searchParams: string) => {
    setFacilityCareTeamData((prev) => {
      return {
        ...prev,
        loading: true,
      };
    });

    axios
      .get(
        `provider_directory/facility_care_team${searchParams.startsWith("?") ? "" : "?"}${searchParams}&page_size=0`,
      )
      .then(
        (
          res: AxiosResponse<ProviderDirectoryFacilityCareTeamList200Response>,
        ) => {
          const facilities: Record<string, FacilityWithCareTeamsProps> = {};

          res.data.result?.results.forEach((facilityCareTeam) => {
            if (facilityCareTeam.facility && facilityCareTeam.facility.uuid) {
              if (!facilities[facilityCareTeam.facility.uuid]) {
                facilities[facilityCareTeam.facility.uuid] = {
                  ...facilityCareTeam.facility,
                  care_teams: [facilityCareTeam.care_team],
                };
              } else {
                facilities[facilityCareTeam.facility.uuid].care_teams.push(
                  facilityCareTeam.care_team,
                );
              }
            }
          });

          const facilitiesArray = Object.values(facilities).sort((a, b) =>
            (a.reporting_name ?? "").localeCompare(b.reporting_name ?? ""),
          );

          setFacilityCareTeamData({
            results: facilitiesArray,
            loading: false,
            error: null,
          });
        },
      )
      .catch((err) => {
        console.error(err);
        setFacilityCareTeamData({
          results: [],
          loading: false,
          error: "Something went wrong loading facilities",
        });
      });
  }, []);

  const setFacilityCareTeams = useCallback(
    (data: Partial<FacilityCareTeamData>) => {
      setFacilityCareTeamData((prev) => {
        return {
          ...prev,
          ...data,
        };
      });
    },
    [],
  );

  const setFacilityCareTeamResults = useCallback(
    (results: FacilityWithCareTeamsProps[]) => {
      setFacilityCareTeamData((prev) => {
        return {
          ...prev,
          results,
          loading: false,
        };
      });
    },
    [],
  );

  const contextValue: FacilityCareTeamContextType = {
    facilityCareTeamData,
    fetchFacilityCareTeams,
    setFacilityCareTeams,
    facilityCareTeamResults: facilityCareTeamData.results,
    setFacilityCareTeamResults,
  };

  return (
    <FacilityCareTeamContext.Provider value={contextValue}>
      {children}
    </FacilityCareTeamContext.Provider>
  );
};

export const useFacilityCareTeams = () => {
  const context = useContext(FacilityCareTeamContext);

  if (context === undefined) {
    throw new Error(
      "useFacilityCareTeams must be used within a FacilityCareTeamProvider",
    );
  }

  return context;
};
